
import React, { createContext, useContext, useState, ReactNode } from 'react';

interface UserInfo {
  email: string;
  name: string;
  image_url?: string;
}

interface SessionState {
  onboardingRequired: boolean;
  sessionInitialized: boolean;
  userInfo: UserInfo | null;
  tempToken: string | null;
  playerId: string | null;
  setSession: (data: any) => void;
  clearSession: () => void;
}

const SessionContext = createContext<SessionState | undefined>(undefined);

export const SessionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [onboardingRequired, setOnboardingRequired] = useState(false);
  const [sessionInitialized, setSessionInitialized] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [tempToken, setTempToken] = useState<string | null>(null);
  const [playerId, setPlayerId] = useState<string | null>(null);

  const setSession = (data: any) => {
    if (data.onboarding_required) {
      setOnboardingRequired(true);
      setUserInfo(data.user_info);
      setTempToken(data.temp_token);
      setPlayerId(null);
    } else {
      // For standard login, store the player_id
      setOnboardingRequired(false);
      setUserInfo(null);
      setTempToken(null);
      setPlayerId(data.player_id || null);
    }
    // Mark session as initialized after processing the session data
    setSessionInitialized(true);
  };

  const clearSession = () => {
    setOnboardingRequired(false);
    setUserInfo(null);
    setTempToken(null);
    setPlayerId(null);
    setSessionInitialized(false);
  };

  return (
    <SessionContext.Provider value={{ onboardingRequired, sessionInitialized, userInfo, tempToken, playerId, setSession, clearSession }}>
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};
